/**
 * Tracking Manager
 * 
 * This utility manages Google Tag Manager integration based on user cookie preferences.
 * It initializes GTM and provides methods for tracking events.
 */
import { createGtm } from '@gtm-support/vue-gtm';

// GTM instance
let gtmInstance = null;

/**
 * Initialize Google Tag Manager if allowed by user preferences
 * 
 * @param {Array} cookiePreferences - Array of allowed cookie categories
 * @param {Object} router - Vue Router instance
 * @returns {Object|null} - GTM instance or null if initialization failed
 */
export function initializeGTM(cookiePreferences, router = null) {
  // Only initialize if statistics cookies are allowed
  const statisticsAllowed = cookiePreferences.includes('statistics');
  
  // Get GTM ID from config
  const gtmId = window.vueAppConfig?.settings?.google_tag_manager;
  
  //console.log('Initializing GTM with preferences:', { statisticsAllowed, gtmId });
  
  // If no GTM ID or statistics not allowed, don't initialize
  if (!statisticsAllowed || !gtmId) {
   // console.log('GTM not initialized - statistics not allowed or GTM ID not found');
    return null;
  }
  
  // Create GTM instance
  try {
    const gtmConfig = {
      id: gtmId,
      enabled: true,
      debug: process.env.NODE_ENV !== 'production',
      // Don't load the script since it should already be in the container
      loadScript: false,
      // Use dataLayer as the existing global variable
      vueGtmPlugin: {
        enabled: true,
        ignoredViews: []
      }
    };
    
    // Add router if provided (for automatic page view tracking)
    if (router) {
      gtmConfig.vueRouter = router;
    }
    
    gtmInstance = createGtm(gtmConfig);
    //console.log('GTM initialized successfully with ID:', gtmId);
    
    // Push consent information to dataLayer with full Consent Mode parameters
    window.dataLayer = window.dataLayer || [];
    
    const marketingConsent = cookiePreferences.includes('marketing');
    
    window.dataLayer.push({
      'event': 'consent_update',
      // Google Consent Mode v2 parameters
      'analytics_storage': 'granted',  // For statistics consent
      'ad_storage': marketingConsent ? 'granted' : 'denied',  // Cookie storage for ads
      'ad_user_data': marketingConsent ? 'granted' : 'denied', // User data for ads
      'ad_personalization': marketingConsent ? 'granted' : 'denied', // Personalized advertising
      'functionality_storage': 'granted', // For essential website functionality
      'security_storage': 'granted', // For security purposes, always granted
      
      // Keep backward compatibility with existing implementation
      'consent_statistics': true,
      'consent_marketing': marketingConsent
    });
    
    return gtmInstance;
  } catch (error) {
    console.error('Error initializing GTM:', error);
    return null;
  }
}

/**
 * Get the current GTM instance
 * @returns {Object|null} - GTM instance if initialized, null otherwise
 */
export function getGTM() {
  return gtmInstance;
}

/**
 * Check if GTM is currently enabled
 * @returns {boolean} - True if GTM is enabled, false otherwise
 */
export function isGTMEnabled() {
  return gtmInstance && gtmInstance.enabled();
}

/**
 * Initialize tracking based on cookie preferences
 * @param {Array} cookiePreferences - Array of allowed cookie categories
 * @param {Object} router - Vue Router instance
 */
export function initializeTracking(cookiePreferences, router = null) {
  initializeGTM(cookiePreferences, router);
}

/**
 * Track a page view
 * @param {string} path - The current page path
 * @param {string} title - The page title
 */
export function trackPageView(path, title) {
  try {
    // Get cookie preferences from localStorage
    const cookieConsent = localStorage.getItem('cookie-comply');
    if (!cookieConsent) return;
    
    // Parse preferences
    let preferences = [];
    if (cookieConsent === 'all') {
      preferences = ['technical', 'statistics', 'marketing'];
    } else {
      try {
        preferences = JSON.parse(cookieConsent);
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
        return;
      }
    }
    
    // Check if statistics are allowed
    const statisticsAllowed = preferences.includes('statistics');
    
    // Track page view if GTM is initialized and statistics are allowed
    if (statisticsAllowed && gtmInstance && path) {
      gtmInstance.trackView(title || document.title, path);
      //console.log('Tracked page view:', { title: title || document.title, path });
    }
  } catch (error) {
    console.error('Error tracking page view:', error);
  }
}

/**
 * Track a custom event
 * @param {string} category - Event category 
 * @param {string} action - Event action
 * @param {string} label - Event label
 * @param {number} value - Event value (optional)
 * @param {Object} additionalParams - Additional parameters for the event
 */
export function trackEvent(category, action, label, value = null, additionalParams = {}) {
  try {
    // Get cookie preferences from localStorage
    const cookieConsent = localStorage.getItem('cookie-comply');
    if (!cookieConsent) return;
    
    // Parse preferences
    let preferences = [];
    if (cookieConsent === 'all') {
      preferences = ['technical', 'statistics', 'marketing'];
    } else {
      try {
        preferences = JSON.parse(cookieConsent);
      } catch (e) {
        console.error('Error parsing cookie preferences:', e);
        return;
      }
    }
    
    // Check if statistics are allowed
    const statisticsAllowed = preferences.includes('statistics');
    
    // Track event if GTM is initialized and statistics are allowed
    if (statisticsAllowed && gtmInstance) {
      const eventData = {
        category,
        action,
        label,
        ...additionalParams
      };
      
      if (value !== null) {
        eventData.value = value;
      }
      
      const formattedEvent = {
        event: 'interaction',
        ...eventData
      };
      
      // Use the correct method to track events
      if (gtmInstance.trackEvent) {
        gtmInstance.trackEvent(formattedEvent);
      } else if (gtmInstance.dataLayer) {
        // Alternative: Push directly to dataLayer
        gtmInstance.dataLayer.push(formattedEvent);
      } else {
        // Fallback to window.dataLayer
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(formattedEvent);
      }
      // console.log('Tracked event:', formattedEvent);
    }
  } catch (error) {
    console.error('Error tracking event:', error);
  }
}

export default {
  initializeGTM,
  initializeTracking,
  trackPageView,
  trackEvent,
  getGTM,
  isGTMEnabled
};